import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";

// Connects to data-controller="modal"
//
// Supports two modes for show/hide:
// 1. Target-based: wrap the <dialog> and add data-modal-target="dialog"
// 2. ID-based: set data-modal-id-value="my_dialog_id" to reference a dialog elsewhere in the DOM
//
// Optional values:
// - data-modal-reset-on-open-value="true" — reset forms inside the dialog each time show() is called
// - data-modal-refresh-url-value="..." — fallback URL for Turbo.visit after modal form success
//
export default class extends Controller {
  static targets = ["dialog"];
  static values = {
    id: String,
    resetOnOpen: { type: Boolean, default: false },
    refreshUrl: String,
  };

  connect() {
    this.boundHandleSubmitEnd = this.#handleSubmitEnd.bind(this);
    document.addEventListener("turbo:submit-end", this.boundHandleSubmitEnd);
  }

  disconnect() {
    if (this.boundHandleSubmitEnd) {
      document.removeEventListener(
        "turbo:submit-end",
        this.boundHandleSubmitEnd,
      );
    }
  }

  show() {
    const dialog = this.#dialog;
    if (!dialog) {
      console.warn("[modal] No dialog target configured");
      return;
    }

    document.querySelectorAll("dialog").forEach((openDialog) => {
      if (openDialog !== dialog && openDialog.open) openDialog.close();
    });

    if (this.resetOnOpenValue) {
      dialog.querySelectorAll("form").forEach((form) => form.reset());
    }
    dialog.showModal();
  }

  clickRowLink(event) {
    const target =
      event.target instanceof Element ? event.target : event.target.parentElement;
    if (target?.closest("a, button")) return;
    this.element.querySelector("a[data-turbo-frame]")?.click();
  }

  hide() {
    const dialog = this.#dialog;
    if (!dialog) return;

    dialog.close();
  }

  get #dialog() {
    if (this.hasIdValue) {
      const el = document.getElementById(this.idValue);
      if (!el) {
        console.warn(`[modal] Element with id "${this.idValue}" not found`);
      }
      return el;
    }
    if (this.hasDialogTarget) return this.dialogTarget;

    return null;
  }

  #handleSubmitEnd(event) {
    const { success, fetchResponse } = event.detail;

    // Only act on successful submissions (2xx status codes)
    // For validation errors (422), keep the modal open to show errors
    if (!success) return;

    const status = fetchResponse?.response?.status;
    if (status && status >= 400) return;

    const form =
      event.target instanceof HTMLFormElement
        ? event.target
        : event.target?.closest?.("form");
    if (!(form instanceof HTMLFormElement)) return;

    const controllerDialog = this.#dialog;
    if (!controllerDialog?.contains?.(form)) return;

    const turboVisitUrl = fetchResponse?.response?.headers?.get("X-Turbo-Visit");
    const keepModalOpen =
      fetchResponse?.response?.headers?.get("X-Modal-Keep-Open") === "true";
    const contentType =
      fetchResponse?.response?.headers?.get("Content-Type") || "";
    const turboStreamResponse = contentType.includes(
      "text/vnd.turbo-stream.html",
    );

    if (form.hasAttribute("data-modal-no-refresh")) {
      const closeOnSuccess =
        form.hasAttribute("data-close-modal-on-success") && !keepModalOpen;

      if (closeOnSuccess || turboVisitUrl) {
        controllerDialog.close();
      }
      if (turboVisitUrl) Turbo.visit(turboVisitUrl);
      return;
    }

    if (turboStreamResponse) return;

    const dialog = form.closest("dialog");
    if (dialog !== controllerDialog) return;

    dialog.close();

    // Refresh page via Turbo Drive visit (no frame flicker, preserves scroll via scroll-preserve)
    // Check for a fresh URL inside the dialog first (updated by turbo stream replacements),
    // then fall back to the static Stimulus value, then the current URL.
    const refreshEl = dialog.querySelector("[data-modal-refresh-url]");
    const url = refreshEl?.dataset.modalRefreshUrl
      || (this.hasRefreshUrlValue ? this.refreshUrlValue : null)
      || window.location.href;
    Turbo.visit(url, { action: "replace" });
  }
}
